import { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';
import { axiosRequest } from '../utils/axiosRequest';
import { retrieveItem } from '../utils/helpers';
import {
  ITranscriptSentence,
  ISearchResults,
  IAIScore,
} from '../global/interfaces';
import { IDocumentParagraph } from '@homeporter/types';

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const useSearch = (documentParagraphMap: Map<string, IDocumentParagraph>) => {
  const axios = axiosRequest();

  const [selectedSentenceId, setSelectedSentenceId] = useState<string>('');

  const transcriptSearchResultsMapRef = useRef<Map<string, ISearchResults>>(
    new Map(),
  );
  const [semanticSearch, setSemanticSearch] =
    useState<ITranscriptSentence | null>(null);

  const { isLoading: searchLoading, isError: searchError } = useQuery(
    ['senetence_search', semanticSearch],
    async () => {
      if (!semanticSearch) return;

      console.log('do search_library query:', semanticSearch);
      try {
        const { data } = await axios.get(`/ai/search_library`, {
          params: { sentence: semanticSearch },
        });

        //await sleep(1000)

        console.log('fetchSearchResults:', data);
        const searchResult = data.data;

        const scores: IAIScore[] = searchResult.scores.map((item: IAIScore) => {
          if (item.ref && item.text) {
            documentParagraphMap.set(`${item.did}-${item.idx}`, {
              did: item.did,
              idx: item.idx,
              ref: item.ref,
              text: item.text,
            });
          }
          return { did: item.did, idx: item.idx, score: item.score };
        });

        console.log('searchResult:', searchResult);

        const _searchResult: ISearchResults = {
          sid: searchResult.sid,
          scores: scores,
        };
        if (!searchResult.sid) {
          _searchResult.text = searchResult.text;
        } else {
          transcriptSearchResultsMapRef.current.set(
            searchResult.sid,
            _searchResult,
          );
        }
        updateSearchResults(_searchResult);
      } catch (error) {
        console.error('Error fetching search results:', error);
        searchQueueRef.current = searchQueueRef.current.slice(1);
      }
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!semanticSearch,
      retry: 1,
    },
  );

  const [searchResults, setSearchResults] = useState<ISearchResults>();
  const processSentenceSearchResults = (sentence: ITranscriptSentence) => {
    if (!sentence) return;

    console.log(
      `::::processSentenceSearchResults(${JSON.stringify(sentence)})`,
    );

    if (
      sentence.sid &&
      transcriptSearchResultsMapRef.current.has(sentence.sid)
    ) {
      console.log(
        'from cache sentence.sid: ' + sentence.sid,
        transcriptSearchResultsMapRef.current.get(sentence.sid),
      );

      const searchResults = transcriptSearchResultsMapRef.current.get(
        String(sentence.sid),
      );
      if (searchResults) {
        updateSearchResults(searchResults);
      }
    } else {
      setSemanticSearch(sentence);
    }
  };

  const updateSearchResults = (searchResults: ISearchResults) => {
    setSearchResults(searchResults);

    console.log('updateSearchResults:', searchResults);
    console.log('test searchQueueRef.current[0]: ', searchQueueRef.current[0]);

    if (
      searchQueueRef.current.length > 0 &&
      searchQueueRef.current[0].sid === searchResults.sid
    ) {
      console.log('slice...');
      searchQueueRef.current = searchQueueRef.current.slice(1);
    }

    if (searchQueueRef.current.length > 0) {
      processSentenceSearchResults(searchQueueRef.current[0]);
    }

    setSelectedSentenceId(!searchResults.sid ? '' : searchResults.sid);
  };

  const searchQueueRef = useRef<ITranscriptSentence[]>([]);
  const searchEnqueue = (sentence: ITranscriptSentence) => {
    console.log('searchEnqueue: ' + JSON.stringify(sentence));

    if (!sentence.sid) {
      searchQueueRef.current.forEach((item, index) => {
        if (!item.sid) {
          searchQueueRef.current.splice(index, 1);
        }
      });
    }

    searchQueueRef.current.unshift(sentence);

    if (searchLoading) return console.log('added to queue...');
    processSentenceSearchResults(searchQueueRef.current[0]);
  };

  const clearSearchResults = () => {
    setSearchResults(undefined);
  };

  return {
    selectedSentenceId,
    setSelectedSentenceId,

    searchLoading,
    searchError,

    searchEnqueue,

    searchResults,
    clearSearchResults,
    updateSentence: processSentenceSearchResults,

    transcriptSearchResultsMapRef,
  };
};

export default useSearch;
